import { useMemo } from "react";

import { Box, Chip, Typography } from "@braintrust/braintrust-ui-components";
import {
  ReviewBeforeSendingSummaryItem,
  type ReviewBeforeSendingSummaryItemProps,
} from "@js/apps/jobs/components";
import { useGetRoleName } from "@js/apps/roles/hooks";
import { useGetSkillsByIds } from "@js/apps/skills/hooks";
import { sanitize } from "@js/services/dom-sanitize";
import type { JobFormValues } from "@js/types/jobs";
import {
  assertUnreachable,
  formatBudget,
  formatCurrency,
  getEnumLabel,
  pluralize,
} from "@js/utils";

type StatusesForReviewDraftsProps<
  TKeys extends keyof JobFormValues = keyof JobFormValues,
> = Pick<ReviewBeforeSendingSummaryItemProps, "isError" | "onEditClick"> & {
  values: Partial<Pick<JobFormValues, TKeys>>;
};

export const StatusForTitle = ({
  values,
  onEditClick,
}: StatusesForReviewDraftsProps<"title">) => {
  const jobTitle = values.title;

  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="Job title"
      sectionContent={jobTitle}
      onEditClick={onEditClick}
      isError={!jobTitle}
    />
  );
};

export const StatusForCategory = ({
  values,
  onEditClick,
}: StatusesForReviewDraftsProps<"role">) => {
  const roleValue = values.role;
  const roleName = useGetRoleName(
    typeof roleValue === "number" ? roleValue : undefined,
  );

  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="Category"
      sectionContent={roleName}
      onEditClick={onEditClick}
      isError={!roleName}
    />
  );
};

type StatusForProjectDetailsProps = StatusesForReviewDraftsProps<
  | "timezones"
  | "locations"
  | "expected_hours_per_week"
  | "contract_type"
  | "payment_type"
  | "talent_invoice_editing_disabled"
> & {
  nonSupportedLocationWarnings?: string[];
};

export const StatusForProjectDetails = ({
  values,
  onEditClick,
  nonSupportedLocationWarnings = [],
}: StatusForProjectDetailsProps) => {
  const {
    timezones,
    locations,
    expected_hours_per_week,
    contract_type,
    payment_type,
  } = values;

  const expectedHoursIsRequired =
    payment_type !== ENUMS.JobPaymentType.FIXED_PROJECT;
  const isError = expectedHoursIsRequired
    ? !contract_type || !expected_hours_per_week
    : !contract_type;

  const formattedTimezoneValue = useMemo(() => {
    if (!timezones) {
      return "";
    }
    return timezones
      .map((timezone) => {
        return timezone.value;
      })
      .join(", ");
  }, [timezones]);

  const contractTypeLabel = contract_type
    ? getEnumLabel(ENUMS.JobContractTypeLabels, contract_type)
    : "";

  const hasWarnings = nonSupportedLocationWarnings.length > 0;

  const expectedHoursLabel = expected_hours_per_week
    ? `${expected_hours_per_week} hours`
    : "Upon completion";

  const timezoneLabel = formattedTimezoneValue || "No time zone requirement";

  const detailsLine = useMemo(() => {
    return [contractTypeLabel, expectedHoursLabel, timezoneLabel]
      .filter(Boolean)
      .join(" · ");
  }, [contractTypeLabel, expectedHoursLabel, timezoneLabel]);

  const sectionContent = useMemo(() => {
    if (!locations?.length) {
      return detailsLine;
    }
    return (
      <Box>
        <Box component="span">{detailsLine}</Box>
        {!!locations.length && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
            {locations.map((location) => {
              const name = location?.location ?? "";
              const isNonSupported =
                nonSupportedLocationWarnings.includes(name);

              return (
                <Chip
                  key={name}
                  color="grey-4"
                  size="x-small"
                  sx={{
                    px: "10px !important",
                    background: isNonSupported
                      ? "#B04D4A1A !important"
                      : undefined,
                    borderColor: isNonSupported
                      ? "#B04D4A52 !important"
                      : undefined,
                  }}
                  label={
                    <Typography
                      component="span"
                      fontWeight={500}
                      className={isNonSupported ? "dot-prefix" : undefined}
                      sx={{
                        fontSize: "12px !important",
                        pl: isNonSupported ? "13px !important" : undefined,
                        color: isNonSupported ? "#B04D4A" : undefined,
                        "--dot-size": "7px",
                      }}
                    >
                      {name}
                    </Typography>
                  }
                />
              );
            })}
          </Box>
        )}
      </Box>
    );
  }, [detailsLine, locations, nonSupportedLocationWarnings]);

  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="Project details"
      sectionContent={sectionContent}
      ellipsis={false}
      onEditClick={hasWarnings ? undefined : onEditClick}
      isError={isError}
      isWarning={hasWarnings}
      sx={hasWarnings ? warningStyles : undefined}
    />
  );
};

const warningStyles = {
  borderLeft: "3px solid #E8B500",
  background: "#FFD93D14",
} as const;

export const StatusForSkills = ({
  values,
  onEditClick,
}: StatusesForReviewDraftsProps<"new_skills" | "top_skills">) => {
  const { new_skills, top_skills: topSkills } = values;
  const newSkills = useMemo(() => new_skills ?? [], [new_skills]);
  const hasSkills = !!newSkills.length;
  const hasTopSkill = !!topSkills?.length;
  const isError = !hasSkills || !hasTopSkill;
  const { data: skills } = useGetSkillsByIds({ ids: newSkills });

  const skillsLabels = useMemo(
    () =>
      newSkills.map((id) => {
        return skills.find((skill) => skill.id === id)?.name ?? "";
      }),
    [newSkills, skills],
  );

  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="Skills"
      sectionContent={skillsLabels.join(", ")}
      onEditClick={onEditClick}
      isError={isError}
    />
  );
};

export const StatusForExperience = ({
  onEditClick,
  values,
}: StatusesForReviewDraftsProps<"experience_level">) => {
  const experience = values.experience_level;
  const experienceLabel = experience
    ? getEnumLabel(ENUMS.JobExperienceLevelLabels, experience)
    : "None";
  const isError = !experience;

  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="Experience"
      sectionContent={experienceLabel}
      onEditClick={onEditClick}
      isError={isError}
    />
  );
};

export const StatusForRate = ({
  values,
  onEditClick,
}: StatusesForReviewDraftsProps<
  | "hourly_rate"
  | "min_rate"
  | "max_annual_rate"
  | "max_rate"
  | "min_annual_rate"
  | "fixed_rate"
  | "payment_type"
  | "min_monthly_rate"
  | "max_monthly_rate"
>) => {
  const {
    hourly_rate,
    min_rate,
    max_rate,
    fixed_rate,
    min_annual_rate,
    max_annual_rate,
    min_monthly_rate,
    max_monthly_rate,
    payment_type: paymentType,
  } = values;

  const checkIsMissingData = () => {
    if (!paymentType) {
      return;
    }

    switch (paymentType) {
      case ENUMS.JobPaymentType.PER_TASK:
      case ENUMS.JobPaymentType.FIXED_PRICE:
      case ENUMS.JobPaymentType.FIXED_PROJECT: {
        return !fixed_rate;
      }
      case ENUMS.JobPaymentType.HOURLY: {
        if (hourly_rate !== "range") {
          return !hourly_rate;
        }

        return !min_rate && !max_rate;
      }
      case ENUMS.JobPaymentType.ANNUAL: {
        return !min_annual_rate && !max_annual_rate;
      }
      case ENUMS.JobPaymentType.MONTHLY: {
        return !min_monthly_rate && !max_monthly_rate;
      }
      default: {
        assertUnreachable(paymentType);
        return false;
      }
    }
  };

  const formattedValue = () => {
    const options = { removeDecimal: true };

    const hrSuffix = "/hr";

    if (!paymentType) {
      return;
    }

    switch (paymentType) {
      case ENUMS.JobPaymentType.PER_TASK:
      case ENUMS.JobPaymentType.FIXED_PRICE:
      case ENUMS.JobPaymentType.FIXED_PROJECT: {
        return fixed_rate ? `${formatCurrency(fixed_rate, options)}` : "";
      }
      case ENUMS.JobPaymentType.HOURLY: {
        if (hourly_rate !== "range") {
          return hourly_rate
            ? `${formatCurrency(hourly_rate, options)}${hrSuffix}`
            : "";
        }

        const rangeBudgetWithoutSigns = formatBudget(
          Math.round(Number(min_rate)),
          Math.round(Number(max_rate)),
          {
            paymentType,
            removeDecimal: true,
            hourlyRateSuffix: hrSuffix,
          },
        ).replaceAll("$", "");

        return `$${rangeBudgetWithoutSigns}`;
      }
      case ENUMS.JobPaymentType.ANNUAL: {
        if (!min_annual_rate && !max_annual_rate) {
          return undefined;
        }

        const rangeBudgetWithoutSigns = formatBudget(
          Math.round(Number(min_annual_rate)),
          Math.round(Number(max_annual_rate)),
          {
            paymentType,
            removeDecimal: true,
          },
        ).replaceAll("$", "");

        return `$${rangeBudgetWithoutSigns}`;
      }
      case ENUMS.JobPaymentType.MONTHLY: {
        if (!min_monthly_rate && !max_monthly_rate) {
          return undefined;
        }

        const rangeBudgetWithoutSigns = formatBudget(
          Math.round(Number(min_monthly_rate)),
          Math.round(Number(max_monthly_rate)),
          {
            paymentType,
            removeDecimal: true,
          },
        ).replaceAll("$", "");

        return `$${rangeBudgetWithoutSigns}`;
      }
      default: {
        assertUnreachable(paymentType);
        return "";
      }
    }
  };

  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="Rate"
      sectionContent={formattedValue()}
      onEditClick={onEditClick}
      isError={checkIsMissingData()}
    />
  );
};

export const StatusForJobDescription = ({
  values,
  onEditClick,
}: StatusesForReviewDraftsProps<"description" | "introduction">) => {
  const introductionContent = values.description || values.introduction || "";
  const strippedString = sanitize(introductionContent, {
    ALLOWED_TAGS: [],
  }).replace(/(\&nbsp;)/gm, " ");

  const isError = !introductionContent;

  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="Job description"
      sectionContent={strippedString}
      onEditClick={onEditClick}
      isError={isError}
    />
  );
};

export const StatusForApplicationQuestions = ({
  onEditClick,
  values,
}: StatusesForReviewDraftsProps<"new_application_questions">) => {
  const new_application_questions = values.new_application_questions
    ? values.new_application_questions.filter((q) => q.question)
    : [];

  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="Application questions"
      sectionContent={
        !!new_application_questions?.length
          ? `${
              new_application_questions?.length
            } application question${pluralize(
              new_application_questions?.length,
            )}`
          : "None"
      }
      onEditClick={onEditClick}
    />
  );
};

export const StatusForAiInterview = ({
  values,
}: {
  values: Pick<JobFormValues, "is_ai_interview_enabled">;
}) => {
  const is_ai_interview_enabled = !!values.is_ai_interview_enabled;

  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="AI interview"
      sectionContent={`AI interviewing is ${is_ai_interview_enabled ? "enabled" : "disabled"}`}
    />
  );
};
